import React from 'react'
import { connect } from 'react-redux'
import { getActiveLanguage, getTranslate } from 'react-localize-redux'
import {
  Button,
  ButtonGroup,
  Checkbox,
  Content,
  Dialog,
  DialogContainer,
  Divider,
  Heading,
  Item,
  Picker,
  Section,
  Text,
  TextField,
  Well
} from '@adobe/react-spectrum'
import CheckmarkCircle from '@spectrum-icons/workflow/CheckmarkCircle'
import { getCountryGroups } from '../../common/country-options'
import {
  getChartLineStyleOptions,
  getChartMeasurementOptions,
  getEmployeeRangeOptions,
  getEquipmentUseOptions,
  getLanguageOptions,
  getOrganisationActivityOptions,
  getWiremapDisplayPreferenceOptions
} from '../../common/organisation-preferences'
import {
  getOrganisationPreferences,
  updateOrganisationPreferences
} from '../../redux/actions/organisationPreferencesActions'

const canEditPreferences = profile => {
  if (!profile || !profile.roles) return false

  const roles = Array.isArray(profile.roles)
    ? profile.roles
    : String(profile.roles).split(',')

  return roles.some(role => ['clientadmin', 'clientmanager'].includes(String(role).trim().toLowerCase()))
}

const toFormData = data => ({
  address: data.address || '',
  city: data.city || '',
  state: data.state || '',
  country: data.country || '',
  zipcode: data.zipcode || '',
  language: data.language || 'en-GB',
  chartmeasurement: data.chartmeasurement || 'Meters',
  chartlinestyle: data.chartlinestyle || 'Standard',
  wiremapdisplaypreference: data.wiremapdisplaypreference || 'Standard',
  organisationactivities: data.organisationactivities || [],
  organisationactivityother: data.organisationactivityother || '',
  fieldtechnicianrange: data.fieldtechnicianrange || '',
  equipmentusage: data.equipmentusage || [],
  equipmentusageother: data.equipmentusageother || '',
  testingapplications: data.testingapplications || ''
})

const styles = {
  dialog: {
    width: 'min(820px, calc(100vw - 32px))'
  },
  progress: {
    display: 'grid',
    gridTemplateColumns: 'repeat(3, 1fr)',
    gap: '8px',
    margin: '0 0 20px'
  },
  step: {
    borderBottom: '3px solid #d5dce5',
    color: '#6b7280',
    fontSize: '13px',
    fontWeight: 600,
    padding: '0 0 9px'
  },
  activeStep: {
    borderBottom: '3px solid #1c408c',
    color: '#1c408c'
  },
  body: {
    maxHeight: 'calc(100vh - 340px)',
    minHeight: 0,
    overflowX: 'hidden',
    overflowY: 'auto',
    paddingRight: '4px'
  },
  content: {
    maxHeight: 'calc(100vh - 190px)',
    overflow: 'hidden'
  },
  intro: {
    alignItems: 'center',
    display: 'flex',
    flexDirection: 'column',
    padding: '24px 20px 18px',
    textAlign: 'center'
  },
  introCopy: {
    color: '#4b5563',
    display: 'block',
    fontSize: '16px',
    lineHeight: 1.55,
    margin: '14px auto 24px',
    maxWidth: '580px'
  },
  introHighlights: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
    maxWidth: '500px',
    textAlign: 'left',
    width: '100%'
  },
  introHighlight: {
    alignItems: 'center',
    background: '#f5f8fc',
    border: '1px solid #d9e3f0',
    borderRadius: '6px',
    color: '#294467',
    display: 'flex',
    fontSize: '14px',
    lineHeight: 1.35,
    padding: '14px'
  },
  introHighlightNumber: {
    color: '#1c408c',
    fontSize: '12px',
    fontWeight: 700,
    marginRight: '10px'
  },
  description: {
    color: '#5f6978',
    display: 'block',
    lineHeight: 1.5,
    margin: '4px 0 18px'
  },
  section: {
    background: '#f8fafc',
    border: '1px solid #e1e7ef',
    marginBottom: '14px',
    padding: '16px'
  },
  sectionTitle: {
    color: '#243b5a',
    display: 'block',
    fontWeight: 700,
    marginBottom: '14px'
  },
  grid: {
    display: 'grid',
    gap: '16px',
    gridTemplateColumns: 'repeat(2, minmax(0, 1fr))'
  },
  fullWidth: {
    gridColumn: '1 / -1'
  },
  question: {
    marginBottom: '18px'
  },
  questionLabel: {
    color: '#243b5a',
    display: 'block',
    fontWeight: 700,
    marginBottom: '10px'
  },
  checkboxGrid: {
    display: 'grid',
    gap: '7px 16px',
    gridTemplateColumns: 'repeat(2, minmax(0, 1fr))'
  },
  error: {
    background: '#fff0f0',
    borderLeft: '4px solid #d7373f',
    color: '#a61b1f',
    marginTop: '12px',
    padding: '12px'
  },
  footer: {
    display: 'flex',
    justifyContent: 'space-between',
    width: '100%'
  },
  primaryActionButton: {
    backgroundColor: '#1c408c',
    color: '#ffffff'
  },
  completion: {
    alignItems: 'center',
    display: 'flex',
    flexDirection: 'column',
    padding: '34px 20px 28px',
    textAlign: 'center'
  },
  completionCopy: {
    color: '#4b5563',
    display: 'block',
    lineHeight: 1.55,
    margin: '12px auto 22px',
    maxWidth: '500px'
  },
  completionCountdown: {
    color: '#667085',
    display: 'block',
    fontSize: '13px',
    marginTop: '12px'
  },
  progressTrack: {
    background: '#d9e3f0',
    borderRadius: '3px',
    height: '6px',
    maxWidth: '360px',
    overflow: 'hidden',
    width: '100%'
  },
  progressFill: {
    background: '#1c408c',
    height: '100%',
    transition: 'width 1s linear'
  }
}

class OrganisationPreferencesPromptSpectrum extends React.Component {
  state = {
    completed: false,
    countdown: 5,
    dismissed: false,
    errors: {},
    form: null,
    promptReady: false,
    step: 0,
    submitError: ''
  }

  loadedCustomerId = null

  completionTimer = null

  promptDelayTimer = null

  componentDidMount() {
    this.loadPreferences(this.props)
    this.promptDelayTimer = setTimeout(() => this.setState({ promptReady: true }), 5000)
  }

  componentWillReceiveProps(nextProps) {
    this.loadPreferences(nextProps)

    if (nextProps.data && nextProps.data !== this.props.data) {
      this.setState({ form: toFormData(nextProps.data), errors: {}, submitError: '' })
    }
  }

  componentDidUpdate(previousProps, previousState) {
    if (!previousState.completed && this.state.completed) {
      this.startCompletionCountdown()
    }
  }

  componentWillUnmount() {
    this.clearCompletionTimer()
    if (this.promptDelayTimer) clearTimeout(this.promptDelayTimer)
  }

  loadPreferences = props => {
    const { profile } = props
    if (!canEditPreferences(profile) || !profile.customerid || this.loadedCustomerId === profile.customerid) return

    this.loadedCustomerId = profile.customerid
    props.getPreferences().catch(() => {})
  }

  setField = (name, value) => {
    this.setState(previousState => ({
      form: { ...previousState.form, [name]: value },
      errors: { ...previousState.errors, [name]: null },
      submitError: ''
    }))
  }

  setMultiSelect = (name, value, isSelected) => {
    this.setState(previousState => {
      const selected = previousState.form[name] || []
      return {
        form: {
          ...previousState.form,
          [name]: isSelected ? [...selected, value] : selected.filter(item => item !== value)
        },
        errors: { ...previousState.errors, [name]: null },
        submitError: ''
      }
    })
  }

  validate = step => {
    const { form } = this.state
    const { translate } = this.props
    const errors = {}
    const required = name => {
      if (!form[name] || !String(form[name]).trim()) errors[name] = translate('formfield_error_general_required', 'This field is required.')
    }

    if (step === 1) {
      [
        'address',
        'city',
        'state',
        'country',
        'zipcode',
        'language',
        'chartmeasurement',
        'chartlinestyle',
        'wiremapdisplaypreference'
      ].forEach(required)
    }

    if (step === 2) {
      if (!form.organisationactivities.length) errors.organisationactivities = translate('organisation_preferences_activity_required', 'Select at least one activity.')
      if (form.organisationactivities.includes('Other (please specify)') && !form.organisationactivityother.trim()) {
        errors.organisationactivityother = translate('organisation_preferences_other_activity_required', 'Describe the other activity.')
      }
      required('fieldtechnicianrange')
      if (!form.equipmentusage.length) errors.equipmentusage = translate('organisation_preferences_equipment_required', 'Select at least one option.')
      if (form.equipmentusage.includes('Other') && !form.equipmentusageother.trim()) {
        errors.equipmentusageother = translate('organisation_preferences_other_equipment_required', 'Describe the other equipment use.')
      }
      required('testingapplications')
    }

    this.setState({ errors })
    return Object.keys(errors).length === 0
  }

  nextStep = () => {
    const { step } = this.state
    if (step === 0 || this.validate(step)) this.setState({ step: step + 1 })
  }

  previousStep = () => this.setState(previousState => ({
    errors: {},
    step: Math.max(0, previousState.step - 1),
    submitError: ''
  }))

  clearCompletionTimer = () => {
    if (this.completionTimer) {
      clearInterval(this.completionTimer)
      this.completionTimer = null
    }
  }

  startCompletionCountdown = () => {
    this.clearCompletionTimer()
    this.completionTimer = setInterval(() => {
      this.setState(previousState => {
        if (previousState.countdown <= 1) {
          this.clearCompletionTimer()
          return { countdown: 0, dismissed: true }
        }

        return { countdown: previousState.countdown - 1 }
      })
    }, 1000)
  }

  dismiss = () => {
    this.clearCompletionTimer()
    this.setState({ dismissed: true })
  }

  submit = () => {
    if (!this.validate(2)) return

    this.props.updatePreferences(this.state.form).then(response => {
      if (!response.payload.data.success) {
        this.setState({ submitError: response.payload.data.errors || this.props.translate('organisation_preferences_save_error') })
        return
      }
      this.setState({ completed: true, countdown: 5, submitError: '' })
    }).catch(() => this.setState({ submitError: this.props.translate('organisation_preferences_save_error') }))
  }

  renderPicker = (name, label, options) => {
    const { errors, form } = this.state
    return (
      <Picker
        errorMessage={errors[name]}
        isInvalid={Boolean(errors[name])}
        isRequired
        label={label}
        onSelectionChange={key => this.setField(name, key ? String(key) : '')}
        selectedKey={form[name] || null}
        width="100%"
      >
        {options.map(option => {
          const value = option.value || option
          return <Item key={String(value)}>{option.label || option}</Item>
        })}
      </Picker>
    )
  }

  renderCountryPicker = (name, label) => {
    const { activeLanguage, translate } = this.props
    const { errors, form } = this.state
    const { priority, allOtherCountries } = getCountryGroups(activeLanguage)

    return (
      <Picker
        errorMessage={errors[name]}
        isInvalid={Boolean(errors[name])}
        isRequired
        label={label}
        onSelectionChange={key => this.setField(name, key ? String(key) : '')}
        selectedKey={form[name] || null}
        width="100%"
      >
        <Section title={translate('signup_country_priority_group')}>
          {priority.map(country => <Item key={country.code}>{country.name}</Item>)}
        </Section>
        <Section title={translate('signup_country_all_group')}>
          {allOtherCountries.map(country => <Item key={country.code}>{country.name}</Item>)}
        </Section>
      </Picker>
    )
  }

  renderInput = (name, label, options = {}) => {
    const { errors, form } = this.state
    return (
      <TextField
        errorMessage={errors[name]}
        isInvalid={Boolean(errors[name])}
        isMultiline={options.multiline}
        isRequired
        label={label}
        onChange={value => this.setField(name, value)}
        value={form[name]}
        width="100%"
      />
    )
  }

  renderMultiSelect = (name, label, options, otherName) => {
    const { errors, form, } = this.state
    const { translate } = this.props
    const selected = form[name] || []
    const hasOther = selected.includes('Other') || selected.includes('Other (please specify)')

    return (
      <div style={styles.question}>
        <Text UNSAFE_style={styles.questionLabel}>{label}</Text>
        <div style={styles.checkboxGrid}>
          {options.map(option => {
            const value = option.value || option
            const optionLabel = option.label || option
            return (
            <Checkbox
              isSelected={selected.includes(value)}
              key={value}
              onChange={isSelected => this.setMultiSelect(name, value, isSelected)}
            >
              {optionLabel}
            </Checkbox>
            )
          })}
        </div>
        {errors[name] && <Text UNSAFE_style={{ color: '#a61b1f', display: 'block', marginTop: '8px' }}>{errors[name]}</Text>}
        {hasOther && <div style={{ marginTop: '12px' }}>{this.renderInput(otherName, translate('signup_please_specify'))}</div>}
      </div>
    )
  }

  renderStepIndicator = () => {
    const { step } = this.state
    const { translate } = this.props
    const steps = [
      translate('organisation_preferences_step_intro'),
      translate('organisation_preferences_step_organisation'),
      translate('organisation_preferences_step_work')
    ]

    return (
      <div style={styles.progress}>
        {steps.map((label, index) => (
          <div key={label} style={{ ...styles.step, ...(step === index ? styles.activeStep : {}) }}>
            {`${index + 1}. ${label}`}
          </div>
        ))}
      </div>
    )
  }

  renderIntroStep = () => {
    const { translate } = this.props
    return (
      <div style={styles.intro}>
        <Heading level={2}>{translate('organisation_preferences_intro_title')}</Heading>
        <Text UNSAFE_style={styles.introCopy}>{translate('organisation_preferences_intro_copy')}</Text>
        <div style={styles.introHighlights}>
          <div style={styles.introHighlight}>
            <Text UNSAFE_style={styles.introHighlightNumber}>1</Text>
            {translate('organisation_preferences_step_organisation')}
          </div>
          <div style={styles.introHighlight}>
            <Text UNSAFE_style={styles.introHighlightNumber}>2</Text>
            {translate('organisation_report_preferences')}
          </div>
          <div style={styles.introHighlight}>
            <Text UNSAFE_style={styles.introHighlightNumber}>3</Text>
            {translate('organisation_preferences_step_work')}
          </div>
        </div>
      </div>
    )
  }

  renderOrganisationStep = () => {
    const { activeLanguage, languages, translate } = this.props
    const languageOptions = getLanguageOptions(languages, activeLanguage)

    return (
      <React.Fragment>
        <Heading level={2}>{translate('organisation_preferences_step_organisation')}</Heading>
        <Text UNSAFE_style={styles.description}>{translate('organisation_preferences_prompt_subtitle')}</Text>
        <Well UNSAFE_style={styles.section}>
          <Text UNSAFE_style={styles.sectionTitle}>{translate('organisation_address')}</Text>
          <div style={styles.grid}>
            {this.renderCountryPicker('country', translate('country'))}
            {this.renderInput('address', translate('signup_building_name'))}
            {this.renderInput('city', translate('city'))}
            {this.renderInput('state', translate('signup_state_county'))}
            <div style={styles.fullWidth}>{this.renderInput('zipcode', translate('subscription_wizard_postal_code'))}</div>
          </div>
        </Well>
        <Well UNSAFE_style={styles.section}>
          <Text UNSAFE_style={styles.sectionTitle}>{translate('organisation_report_preferences')}</Text>
          <div style={styles.grid}>
            {this.renderPicker('language', translate('language'), languageOptions)}
            {this.renderPicker('chartmeasurement', translate('organisation_report_measurement'), getChartMeasurementOptions(translate))}
            {this.renderPicker('chartlinestyle', translate('organisation_report_chart_style'), getChartLineStyleOptions(translate))}
            {this.renderPicker('wiremapdisplaypreference', translate('organisation_wiremap_preference'), getWiremapDisplayPreferenceOptions(translate))}
          </div>
        </Well>
      </React.Fragment>
    )
  }

  renderWorkStep = () => {
    const { translate } = this.props
    return (
      <React.Fragment>
        <Heading level={2}>{translate('organisation_preferences_step_work')}</Heading>
        <Text UNSAFE_style={styles.description}>{translate('organisation_preferences_work_copy')}</Text>
        <Well UNSAFE_style={styles.section}>
          {this.renderMultiSelect('organisationactivities', translate('organisation_activities'), getOrganisationActivityOptions(translate), 'organisationactivityother')}
          <div style={styles.question}>{this.renderPicker('fieldtechnicianrange', translate('organisation_field_team_size'), getEmployeeRangeOptions(translate))}</div>
          {this.renderMultiSelect('equipmentusage', translate('organisation_equipment_use'), getEquipmentUseOptions(translate), 'equipmentusageother')}
          {this.renderInput('testingapplications', translate('organisation_testing_applications'), { multiline: true })}
        </Well>
      </React.Fragment>
    )
  }

  renderStep = () => {
    switch (this.state.step) {
      case 0: return this.renderIntroStep()
      case 1: return this.renderOrganisationStep()
      default: return this.renderWorkStep()
    }
  }

  renderCompletion = () => {
    const { countdown } = this.state
    const { translate } = this.props
    const progress = ((5 - countdown) / 5) * 100

    return (
      <div style={styles.completion}>
        <CheckmarkCircle color="positive" size="XXL" />
        <Heading level={2} marginTop="size-250">{translate('organisation_preferences_thank_you')}</Heading>
        <Text UNSAFE_style={styles.completionCopy}>{translate('organisation_preferences_thank_you_copy')}</Text>
        <div style={styles.progressTrack}>
          <div style={{ ...styles.progressFill, width: `${progress}%` }} />
        </div>
        <Text UNSAFE_style={styles.completionCountdown}>
          {translate('organisation_preferences_closing_in_seconds', { count: countdown })}
        </Text>
      </div>
    )
  }

  render() {
    const { data, updating, translate } = this.props
    const { completed, dismissed, form, promptReady, step, submitError } = this.state
    const open = Boolean(form && data && data.canUpdate && promptReady && !dismissed && (data.needsOrganisationPreferences || completed))

    if (!open) return null

    return (
      <DialogContainer onDismiss={this.dismiss}>
        <Dialog size="L" UNSAFE_style={styles.dialog}>
          <Heading>{translate('organisation_preferences')}</Heading>
          <Divider />
          {completed ? (
            <Content>{this.renderCompletion()}</Content>
          ) : (
            <React.Fragment>
              <Content UNSAFE_style={styles.content}>
                {this.renderStepIndicator()}
                <div style={styles.body}>{this.renderStep()}</div>
                {submitError && <Well UNSAFE_style={styles.error}>{submitError}</Well>}
              </Content>
              <ButtonGroup UNSAFE_style={styles.footer}>
                <Button variant="secondary" onPress={this.dismiss} isDisabled={updating}>
                  {translate('organisation_preferences_not_now')}
                </Button>
                <div>
                  {step > 0 && (
                    <Button variant="secondary" onPress={this.previousStep} isDisabled={updating}>
                      {translate('organisation_preferences_back')}
                    </Button>
                  )}
                  <Button
                    marginStart="size-100"
                    variant="accent"
                    UNSAFE_style={styles.primaryActionButton}
                    onPress={step < 2 ? this.nextStep : this.submit}
                    isDisabled={updating}
                  >
                    {step === 0
                      ? translate('organisation_preferences_get_started')
                      : step === 2
                        ? (updating ? `${translate('organisation_preferences_save')}...` : translate('organisation_preferences_save'))
                        : translate('organisation_preferences_next')}
                  </Button>
                </div>
              </ButtonGroup>
            </React.Fragment>
          )}
        </Dialog>
      </DialogContainer>
    )
  }
}

const mapStateToProps = state => ({
  profile: state.profileReducer.profile,
  data: state.organisationPreferencesReducer.data,
  activeLanguage: getActiveLanguage(state.localeReducer).code,
  languages: state.localeReducer.languages,
  translate: getTranslate(state.localeReducer),
  updating: state.organisationPreferencesReducer.updating
})

const mapDispatchToProps = dispatch => ({
  getPreferences: () => dispatch(getOrganisationPreferences()),
  updatePreferences: data => dispatch(updateOrganisationPreferences(data))
})

export default connect(mapStateToProps, mapDispatchToProps)(OrganisationPreferencesPromptSpectrum)
